import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import businessStructureOptions from '../shared/data/business-structure-options.json';
import { DataStorageService } from './data-storage.service';

interface BusinessStructureForm {
  businessStructure: string;
  businessTaxID: string;
  industryType: string;
  yearsInBusiness: string;
  numberOfEmployees: number;
  grossAnnualRevenue: number;
  referralNumber?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OwnerStateService {
  private isOwnerSubject = new BehaviorSubject<boolean>(false);
  public isOwner$ = this.isOwnerSubject.asObservable();
  private readonly dataStorageService = inject(DataStorageService);

  private businessStructureOptionsSubject = new BehaviorSubject<any[]>(
    (businessStructureOptions as any).businessStructureOptions
  );
  public businessStructureOptions$ = this.businessStructureOptionsSubject.asObservable();

  constructor() {
    // Subscribe to owner state changes to update business structure options
    this.isOwner$.subscribe(isOwner => {
      const allOptions = (businessStructureOptions as any).businessStructureOptions;
      if (!isOwner) {
        // Remove sole proprietorship option when not an owner
        const filteredOptions = allOptions.filter(
          (option: any) => option.value !== 'SOLE_PROPRIETORSHIP'
        );
        this.businessStructureOptionsSubject.next(filteredOptions);

        // Clear sole proprietorship selection if it was selected
        const savedForm = this.dataStorageService.getItem<BusinessStructureForm>('businessStructureForm');
        if (savedForm && savedForm.businessStructure === 'SOLE_PROPRIETORSHIP') {
          // Create a new object to avoid modifying the original
          const updatedForm = { ...savedForm, businessStructure: '' };
          // Route through DataStorageService so the server-side draft is the
          // single source of truth (previously wrote directly to both
          // localStorage and sessionStorage, which could disagree on refresh).
          this.dataStorageService.setItem('businessStructureForm', updatedForm);
        }
      } else {
        // Show all options when is owner
        this.businessStructureOptionsSubject.next(allOptions);
      }
    });
  }

  public setIsOwner(isOwner: boolean): void {
    this.isOwnerSubject.next(isOwner);
  }

  /**
   * Set the SSN of the application owner in the personal details form data
   * 
   * @param ssn 
   */
  public setApplicationOwnerSSN(ssn: string): void {
    const personalDetails = this.dataStorageService.getItem<any>('personalDetailsForm') || {};
    personalDetails.ssn = ssn;
    this.dataStorageService.setItem('personalDetailsForm', personalDetails);
  }

  /**
   * Get the SSN of the application owner from the personal details form data
   * 
   * @returns SSN string or null if not set
   */
  public getApplicationOwnerSSN(): string | null {
    const personalDetails = this.dataStorageService.getItem<any>('personalDetailsForm');
    return personalDetails?.ssn || null;
  }
} 